import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from "react";
import { categories } from "@/mocks/categories";
import { useCurrentLanguage } from "@/hooks/useLanguage";

export default function Hero() {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const lang = useCurrentLanguage();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filtered = categories.filter((c) => {
    const name = t(`cat.${c.key}`).toLowerCase();
    return name.includes(query.toLowerCase()) || query.trim() === "";
  });

  const openCategory = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!dropdownOpen && (e.key === "ArrowDown" || e.key === "Enter")) {
      setDropdownOpen(true);
      return;
    }
    if (!dropdownOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && filtered[highlighted]) {
      e.preventDefault();
      openCategory(filtered[highlighted].url);
      setDropdownOpen(false);
    } else if (e.key === "Escape") {
      setDropdownOpen(false);
    }
  };

  return (
    <section className="relative w-full">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=A%20vibrant%20editorial%20flat%20lay%20of%20colorful%20sneakers%20hoodies%20bags%20and%20accessories%20scattered%20artfully%20across%20a%20warm%20cream%20background%20with%20bold%20pops%20of%20coral%20sage%20terracotta%20and%20mustard%2C%20fresh%20modern%20fashion%20photography%2C%20bright%20airy%20lighting%20with%20an%20energetic%20dynamic%20composition&width=1800&height=1200&seq=hero-bg-01&orientation=landscape&nocache=true"
          alt="USFans Spreadsheet fashion products"
          title="USFans Spreadsheet products"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/35 to-black/55"></div>
      </div>

      <div className="relative z-10 w-full px-4 md:px-6 py-24 md:py-32 flex flex-col items-center text-center">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50/20 backdrop-blur-sm text-background-50 text-xs font-medium mb-6">
          <i className="ri-shield-check-line"></i>
          {t("hero.badge")}
        </span>

        <h1 className="w-full max-w-4xl font-heading font-extrabold text-3xl md:text-5xl text-background-50 leading-tight">
          {t("hero.title")}
        </h1>

        <p className="w-full max-w-2xl mt-6 text-sm md:text-base text-background-100 leading-relaxed">
          {t("hero.subtitle")}
        </p>

        <div className="flex flex-col sm:flex-row items-center gap-4 mt-8">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-external-link-line"></i>
            {t("hero.ctaPrimary")}
          </a>
          <a
            href="#articles"
            className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-background-50 text-foreground-900 font-semibold text-sm hover:bg-background-100 transition-colors cursor-pointer"
          >
            {t("hero.ctaSecondary")}
          </a>
        </div>

        <div ref={dropdownRef} className="relative w-full max-w-xl mt-8">
          <div className="flex items-center gap-2 bg-background-50 rounded-md px-4 py-3">
            <i className="ri-search-line text-foreground-500 text-sm"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setDropdownOpen(true);
                setHighlighted(0);
              }}
              onFocus={() => setDropdownOpen(true)}
              onKeyDown={onKeyDown}
              placeholder={t("hero.searchPlaceholder")}
              className="flex-1 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
            />
            <span className="text-xs text-foreground-400 whitespace-nowrap">{t("categories.dropdownLabel")}</span>
            <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}></i>
          </div>

          {dropdownOpen && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-background-50 rounded-lg border border-background-200 shadow-sm max-h-72 overflow-y-auto z-50">
              {filtered.length === 0 ? (
                <p className="px-4 py-3 text-sm text-foreground-500">No category found</p>
              ) : (
                filtered.map((c, idx) => (
                  <button
                    key={c.key}
                    onClick={() => {
                      openCategory(c.url);
                      setDropdownOpen(false);
                    }}
                    onMouseEnter={() => setHighlighted(idx)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm cursor-pointer transition-colors ${
                      idx === highlighted ? "bg-background-100" : ""
                    }`}
                  >
                    <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100 text-foreground-700">
                      <i className={c.icon}></i>
                    </span>
                    <span className="text-foreground-900 flex-1">{t(`cat.${c.key}`)}</span>
                    <i className="ri-arrow-right-up-line text-foreground-400"></i>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 mt-10">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-3.5 rounded-md bg-accent-500 text-background-50 font-bold text-base hover:bg-accent-600 transition-colors cursor-pointer"
          >
            <i className="ri-table-line"></i>
            {t("cta.usfans")}
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-3.5 rounded-md bg-background-50 text-foreground-900 font-bold text-base hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-shopping-cart-line"></i>
            {t("cta.buynow")}
          </a>
        </div>
      </div>
    </section>
  );
}